'use client';

import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import type { CityCode } from '@loci/city-editions';
import { ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { SurfaceLink } from '@/components/atoms/SurfaceLink';
import { OringMark } from '@/components/atoms/OringMark';
import { cn } from '@/components/atoms/primitives/utils';
import { PageContainer } from '@/components/atoms/PageContainer/PageContainer';
import { useCityIsStaging } from '@/lib/city-context.client';
import { APP_BAND_CTA_LABEL, appBandBody, appBandHeading } from '@/lib/copy/appBandCopy';
import { cityAppEntryUrl, isAppHostHref } from '@/lib/cross-host-urls';
import { formatCount } from '@/lib/format/count';

/** A single hyperlink rendered inside one of the footer columns. */
export interface CityFooterLink {
  /** Destination URL — internal paths use `/`, partner/legal links use absolute URLs. */
  readonly href: string;
  /** Visible link text. */
  readonly label: string;
  /**
   * Optional cardinality rendered as a mono `<small>`. Omitted when 0 / unset
   * (FRE-1339) — never a false zero.
   */
  readonly count?: number;
}

/**
 * Grouped link sets for the City footer. `charlotte` + `loci` are the two
 * labeled columns (FRE-1339); `legal` lives in the bottom strip.
 */
export interface CityFooterLinks {
  readonly charlotte: ReadonlyArray<CityFooterLink>;
  readonly loci: ReadonlyArray<CityFooterLink>;
  readonly legal: ReadonlyArray<CityFooterLink>;
}

/**
 * App hand-off band (WEB-1197). Replaces the account + email funnel on every
 * city surface. Copy comes from `lib/copy/appBandCopy`; the href is
 * `cityAppEntryUrl`.
 */
export interface CityFooterAppBand {
  readonly cityCode: CityCode;
  readonly isStaging?: boolean;
}

export interface CityFooterProps {
  /** City display name used for the lockup alt text and aria-label. */
  cityName: string;
  /** Editorial tagline shown beneath the lockup. */
  tagline: string;
  /**
   * Edition label appended to the copyright line, e.g. "Charlotte edition".
   * Kept distinct from `cityName` so multi-city editions can override
   * formatting without re-deriving the string.
   */
  cityEdition: string;
  /** Latitude in decimal degrees, rendered to 4 places with N/S suffix. */
  lat: number;
  /** Longitude in decimal degrees, rendered to 4 places with E/W suffix. */
  lng: number;
  /** Path to the city lockup SVG. Default points at the Charlotte lettermark. */
  lockupSrc?: string;
  /** All link sets — pure data; the molecule never fetches anything. */
  links: CityFooterLinks;
  /**
   * App hand-off band (WEB-1197). When set, renders `AppBand` as
   * `city-footer-band`: heading, body, and an "Open the app" CTA.
   */
  appBand?: CityFooterAppBand;
  /**
   * Optional live-index status line shown in the brand block, e.g.
   * "1,240 places indexed · updated tonight". Rendered with a pulsing "live"
   * dot. Omitted by default — never fabricate a count; pass real data or leave
   * unset.
   */
  statusLabel?: string;
  /**
   * Host label for the bottom locator, e.g. "clt.loci.city". When provided the
   * locator reads `CITY · {hostLabel} · {coords}`; when omitted only the
   * coordinate pair is shown.
   */
  hostLabel?: string;
  /**
   * Override the year shown in the bottom strip. Tests pass this so renders
   * stay deterministic across calendar years; production omits it and the
   * footer falls back to `new Date().getFullYear()`.
   */
  currentYear?: number;
  /** Additional CSS classes applied to the outer `<footer>`. */
  className?: string;
}

const DEFAULT_LOCKUP_SRC = '/brand/loci-clt-lettermark.svg';

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline-glow focus-visible:ring-offset-2 focus-visible:ring-offset-loci-background';

// FRE-1370 (v3 footer parity) — 13px `--fs-small` (was 14px `text-sm`),
// flush-left (no horizontal padding — the previous `px-2` produced an 8px
// indent the design does not have), skyline-glow on hover, deceleration
// ease. Mobile keeps a 44px min touch target (WCAG 2.5.8); desktop collapses
// to the design's 36px row height since a mouse pointer needs no minimum.
const COL_LINK =
  'inline-flex items-center text-small leading-tight text-fg-2 transition-colors duration-micro ease-expressive-out hover:text-accent-text motion-reduce:transition-none rounded-sm min-h-[44px] desktop:min-h-[36px] min-w-[44px] desktop:min-w-0 py-2 desktop:py-0';

// Legal strip links share the strip's mono/uppercase rhythm.
// FRE-888: rest color uses the AA-safe `--footer-coord-fg` (the global `--fg-3`
// was 4.27:1 on the light footer band).
//
// WEB-853 superseded the second half of that note. It read "hover keeps the
// skyline-glow accent — hover/focus states are exempt from the contrast
// minimum", which was moot at the time because the unlayered `a { color:
// inherit }` reset meant the hover utility painted NOTHING. With the reset
// layered, it paints, and raw `--clt-skyline-glow` is 2.09:1 on the light
// ground. The hover ink is now `--accent-text` — identical in dark, AA-safe
// #0a6363 in light — so the exemption argument no longer has to be made.
const LEGAL_LINK =
  'inline-flex items-center justify-center text-micro tracking-[0.04em] leading-tight text-footer-coord-fg font-mono transition-colors duration-micro ease-expressive-out hover:text-accent-text motion-reduce:transition-none rounded-sm min-h-[44px] desktop:min-h-0 min-w-[44px] desktop:min-w-0 px-2 py-2';

// FRE-1370 — group headings are `<h4>` (the app band's one `<h3>` is the
// footer's own subsection heading; columns nest under that), 12px 600
// uppercase 0.14em `--fg-2`.
const COL_TITLE =
  'mb-3.5 text-caption font-semibold leading-tight tracking-[0.14em] text-fg-2 font-display';

// WEB-1197 — the app-band heading is the footer's one h3:
// clamp(20px,2vw,24px) 600 -0.01em `--fg-1`.
const BAND_HEADING =
  'text-[clamp(20px,2vw,24px)] font-semibold leading-snug tracking-[-0.01em] text-fg-1 font-display';

// WEB-1197 — band body: 13px `--fg-2`, capped at 52ch (DS `.o-footer__band p`).
const BAND_BODY = 'max-w-[52ch] text-small leading-normal text-fg-2';

function isCrossOrigin(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

/**
 * Other-host links open in a new tab, except a hop onto the app host: a
 * signed-in viewer's Tonight / This week links go to `app.<root>/<city>/discover`
 * (WEB-1231 / R25) and must navigate in place.
 */
function opensNewTab(href: string): boolean {
  return isCrossOrigin(href) && !isAppHostHref(href);
}

function formatLatitude(lat: number): string {
  const hemi = lat >= 0 ? 'N' : 'S';
  return `${Math.abs(lat).toFixed(4)}° ${hemi}`;
}

function formatLongitude(lng: number): string {
  const hemi = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lng).toFixed(4)}° ${hemi}`;
}

interface ColumnProps {
  readonly title: string;
  readonly links: ReadonlyArray<CityFooterLink>;
  readonly testId: string;
}

function FooterColumn({ title, links, testId }: ColumnProps): React.ReactElement {
  return (
    <div data-testid={testId}>
      <h4 className={COL_TITLE}>{title}</h4>
      <ul className="m-0 flex list-none flex-col space-y-0.5 p-0">
        {links.map((link) => {
          const external = opensNewTab(link.href);
          const showCount = typeof link.count === 'number' && link.count > 0;
          return (
            <li key={`${link.label}-${link.href}`}>
              <SurfaceLink
                href={link.href}
                className={cn(COL_LINK, FOCUS_RING)}
                aria-label={showCount ? `${link.label}, ${formatCount(link.count)}` : undefined}
                {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
              >
                {link.label}
                {showCount ? (
                  <small className="ml-1.5 text-micro tracking-[0.04em] text-fg-3 font-mono [font-variant-numeric:tabular-nums]">
                    {formatCount(link.count)}
                  </small>
                ) : null}
              </SurfaceLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AppBand({
  cityName,
  cityCode,
  isStaging,
}: {
  readonly cityName: string;
  readonly cityCode: CityCode;
  readonly isStaging?: boolean;
}): React.ReactElement {
  const contextIsStaging = useCityIsStaging();
  const stage = isStaging ?? contextIsStaging;
  const href = cityAppEntryUrl(cityCode, { isStaging: stage });
  const external = isCrossOrigin(href);
  const headingId = React.useId();

  return (
    <div
      data-testid="city-footer-band"
      className={cn(
        // WEB-1197 — DS `.o-footer__band`: one flex row, space-between, wrap.
        // Padding is `--sp-6` / `--sp-7` (24 / 28), collapsing to `--sp-5`
        // at ≤600. Outer rounded/bordered/shadowed chrome is unchanged.
        'mb-12 flex flex-wrap items-center justify-between gap-6 overflow-hidden',
        'rounded-ds-xl border border-ds-border bg-ds-surface px-7 py-6 shadow-[var(--shadow-md)]',
        'max-[600px]:p-5',
      )}
    >
      <div className="flex min-w-0 items-center gap-5">
        <OringMark size={30} className="text-clt-skyline-glow-fg" />
        <div className="min-w-0">
          <h3
            id={headingId}
            data-testid="city-footer-band-heading"
            className={cn('m-0 mb-1', BAND_HEADING)}
          >
            {appBandHeading(cityName)}
          </h3>
          <p data-testid="city-footer-band-body" className={cn('m-0', BAND_BODY)}>
            {appBandBody(cityCode)}
          </p>
        </div>
      </div>
      {/* WEB-1001 — the band CTA is the page's one `--cta` (national Electric
          Amber): the design-system md `cta` button (40; the coarse-pointer
          hit box makes it 44). Full width at ≤600. It owns its focus ring. */}
      <Button asChild variant="cta" className="max-[600px]:w-full">
        <Link
          href={href}
          aria-describedby={headingId}
          data-testid="city-footer-band-cta"
          {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
        >
          {APP_BAND_CTA_LABEL}
          <ArrowUpRight aria-hidden="true" className="h-4 w-4" />
        </Link>
      </Button>
    </div>
  );
}

/**
 * CityFooter — bottom chrome for the marketing-city surface (the public,
 * place-first member of the DS "Global Footers" family; the signed-in `AppFooter`
 * is its sibling).
 *
 * DS 1.4.0 composition per `docs/design-system/1.4.0/templates/city/index.html`
 * `<footer class="o-footer">`:
 *   - Brand spine: a hero-wash gradient bleeds along the top edge and a masked
 *     dot scatter sits behind the content (`overflow-hidden`, sunken ground).
 *   - App band (`appBand`) handing off to the city app ("Open the app").
 *   - Top grid (`1.6fr` brand + two `1fr` labeled columns): lockup, tagline,
 *     optional live-index status line, coordinate pair, then Charlotte / LOCI.
 *   - Bottom bar: a `CITY · host · coords` locator on the left; copyright +
 *     edition + legal links on the right.
 *
 * Client molecule — every link, label, and coord is driven by props. It
 * captures no email (API-1807). Theme tokens come from the parent (`--loci-color-*` /
 * `--fg-*`). Skyline Teal accents use `--clt-skyline-glow`; the band CTA uses
 * national Electric Amber (`--loci-amber`).
 */
export function CityFooter({
  cityName,
  tagline,
  cityEdition,
  lat,
  lng,
  lockupSrc = DEFAULT_LOCKUP_SRC,
  links,
  appBand,
  statusLabel,
  hostLabel,
  currentYear,
  className,
}: CityFooterProps): React.ReactElement {
  const year = currentYear ?? new Date().getFullYear();
  const coordText = `${formatLatitude(lat)} · ${formatLongitude(lng)}`;
  const coordAria = `Coordinates ${formatLatitude(lat)}, ${formatLongitude(lng)}`;

  return (
    <footer
      aria-label={`${cityName} site footer`}
      className={cn(
        'o-footer city-footer relative isolate w-full overflow-hidden border-t border-ds-border bg-bg-sunken',
        // FRE-1370 — `.wrap { padding-block: clamp(40px,5vw,64px) 24px }`.
        // `--footer-pad-top` already matches the top value; bottom is 24px
        // (was 32px).
        'pb-6 pt-footer-pad-top',
        className,
      )}
      data-testid="city-footer"
    >
      {/* Hero wash along the top edge — the brand spine, layer one. */}
      <span aria-hidden="true" className="footer-hero-wash" />
      {/* Dot scatter — map density, never a grid; masked to the top-right. */}
      <span aria-hidden="true" className="footer-dot-scatter" />

      <PageContainer className="relative">
        {appBand ? (
          <AppBand cityName={cityName} cityCode={appBand.cityCode} isStaging={appBand.isStaging} />
        ) : null}

        <div
          className={cn(
            'grid gap-8 mb-12',
            'grid-cols-2 max-[759px]:[&>*:first-child]:col-span-2',
            'min-[760px]:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)]',
          )}
          data-testid="city-footer-top"
        >
          <div className="flex max-w-[340px] flex-col items-start gap-4 md:col-span-1">
            <Link
              href="/"
              aria-label={`LOCI ${cityName}, home`}
              className={cn('inline-flex w-fit shrink-0 items-center rounded-md', FOCUS_RING)}
              data-testid="city-footer-lockup-link"
            >
              <Image
                src={lockupSrc}
                alt={`LOCI · ${cityName}`}
                // FRE-1370 — `.brand img` is 24px tall (was 26px). Width is
                // the SVG's real intrinsic ratio (viewBox 445.12×140.08 ≈
                // 3.18:1) at 24px tall — confirmed against the real browser
                // render (measured 76.25px), not the previous box's ratio.
                width={76}
                height={24}
                className="h-6 w-auto"
              />
            </Link>
            <p
              className="m-0 text-small leading-normal text-fg-2"
              data-testid="city-footer-tagline"
            >
              {tagline}
            </p>
            {statusLabel ? (
              <span
                data-testid="city-footer-status"
                // FRE-888: AA-safe footer meta color on the light band.
                className="inline-flex items-center gap-[7px] text-micro tracking-[0.12em] text-footer-coord-fg font-mono"
              >
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-loci-success shadow-[0_0_7px_var(--loci-color-success)] animate-loci-o-pulse motion-reduce:animate-none"
                />
                {statusLabel}
              </span>
            ) : null}
            <div
              data-testid="city-footer-brand-coord"
              // FRE-888: `--footer-coord-fg` clears WCAG AA on the light footer
              // band (#EEF1F6) where the global `--fg-3` (#6B7280) was 4.27:1.
              className="text-caption whitespace-nowrap tracking-[0.04em] text-footer-coord-fg font-mono [font-feature-settings:'tnum'_1,'zero'_1]"
              aria-label={coordAria}
            >
              {coordText}
            </div>
          </div>

          <FooterColumn
            title={cityName}
            links={links.charlotte}
            testId="city-footer-col-charlotte"
          />
          <FooterColumn title="LOCI" links={links.loci} testId="city-footer-col-loci" />
        </div>

        <div
          className={cn(
            'flex flex-wrap items-center justify-between gap-x-6 gap-y-4 border-t border-ds-border pt-6',
            // FRE-888: copyright + coord baseline color. `--footer-coord-fg` is
            // AA-safe on the light footer band where global `--fg-3` was 4.27:1.
            'text-caption text-footer-coord-fg',
          )}
          data-testid="city-footer-bottom"
        >
          <span
            data-testid="city-footer-locator"
            // FRE-888: coord text uses the AA-safe footer token on the light band.
            className="inline-flex flex-wrap items-center gap-2 text-micro tracking-[0.04em] text-footer-coord-fg font-mono [font-feature-settings:'tnum'_1,'zero'_1]"
          >
            {hostLabel ? (
              <>
                {/* FRE-888: the `CITY · host` accent sat on the light footer band
                    as raw `--clt-skyline-glow` (#4FC4C4) = 1.85:1. Swap to the
                    theme-aware accent-as-text token (#0A6363 light = 6.23:1,
                    #4FC4C4 dark = 9.45:1 on the near-black band). */}
                <b className="font-medium tracking-[0.1em] text-clt-skyline-glow-fg">
                  CITY · {hostLabel}
                </b>
                <span aria-hidden="true" className="text-border-strong">
                  ·
                </span>
              </>
            ) : null}
            <span data-testid="city-footer-coord" aria-label={coordAria}>
              {coordText}
            </span>
          </span>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {/* FRE-1370 — the copyright line is 11px mono 0.04em `tnum` (was
                12px Inter). Scoped to this span, not the whole bottom bar. */}
            <span className="text-micro tracking-[0.04em] font-mono [font-feature-settings:'tnum'_1,'zero'_1]">
              © {year} LOCUS TECHNOLOGIES · {cityEdition}
            </span>
            <div className="flex flex-wrap gap-2" data-testid="city-footer-legal">
              {links.legal.map((link) => {
                const external = isCrossOrigin(link.href);
                return (
                  <Link
                    key={`${link.label}-${link.href}`}
                    href={link.href}
                    className={cn(LEGAL_LINK, FOCUS_RING)}
                    {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </PageContainer>
    </footer>
  );
}
